/**
 * Demo seed data for the boards store, ported from the design mockup.
 * Entities are authored with readable keys ('roadmap', 'kg', 'c1');
 * `uuidifySeed()` rewrites every id and cross-reference to a deterministic
 * UUID, so ids and /boards/<id> URLs survive reseeding. `enrichSeedCards()`
 * derives each card's detail from its title, status and assignee.
 */
import {stableSeedUuid} from '@u/uuid';
import {isString} from 'lodash-es';
import {DateTime} from 'luxon';
import type {
   IApiKey,
   IBoard,
   ICard,
   ICardActivity,
   ICardComment,
   IFolderTreeNode,
   IInvitation,
   IMember,
   INotification,
   IOrganization,
} from './board_demo_data_types';
import {
   ApiKeyOwnerType,
   BoardAccess,
   BoardVisibility,
   CardPriority,
   CardStatus,
   CustomFieldType,
   FolderNodeType,
   InvitationStatus,
   MemberStatus,
   OrgRole,
   SeatType,
} from './board_demo_data_types';

/** Everything the boards store seeds its state from. */
export interface IBoardsSeedData {
   organization: IOrganization;
   currentUserId: string;
   members: IMember[];
   invitations: IInvitation[];
   apiKeys: IApiKey[];
   folderTree: IFolderTreeNode[];
   openFolderIds: string[];
   boards: IBoard[];
   notifications: INotification[];
   currentBoardId: string;
}

// ---------------------------------------------------------------------------
// Decorative palette from the design mockup. Data only; not UI theming.
// ---------------------------------------------------------------------------
const DS = {
   blue400: '#579DFF',
   blue500: '#388BFF',
   blue600: '#1D7AFC',
   blue700: '#0C66E4',
   blue800: '#0055CC',
   purple500: '#8270DB',
   purple600: '#6E5DC6',
   green500: '#22A06B',
   green600: '#1F845A',
   green700: '#216E4E',
   teal500: '#1D9AAA',
   teal600: '#1D7F8C',
   teal700: '#206A83',
   orange400: '#F38A3F',
   orange500: '#F18D13',
   orange700: '#B65C02',
   red500: '#E34935',
   red700: '#CA3521',
   magenta500: '#CD519D',
   yellow500: '#E2B203',
   neutral400: '#8590A2',
   neutral700: '#626F86',
} as const;

/** Swatch choices offered when creating/recoloring a board. */
// ---------------------------------------------------------------------------
// Seed construction
// ---------------------------------------------------------------------------

/** Build a fresh, fully-enriched seed data blob (all entity ids as UUIDs). */
export function createSeedData(): IBoardsSeedData {
   const members = createMembers();
   const boards = createBoards();

   assignBoardShares(boards);
   enrichSeedCards(boards, members);

   const data: IBoardsSeedData = {
      organization: createAcmeOrg(),
      currentUserId: 'kg',
      members,
      invitations: createInvitations(),
      apiKeys: createApiKeys(),
      folderTree: createFolderTree(),
      openFolderIds: ['product'],
      boards,
      notifications: createNotifications(),
      currentBoardId: 'roadmap',
   };

   uuidifySeed(data);
   return data;
}

function createAcmeOrg(): IOrganization {
   return {
      id: 'acme',
      name: 'Acme Inc',
      slug: 'acme-inc',
      plan: 'Business plan',
      domain: 'acme.com',
      billingEmail: 'billing@acme.com',
      seatsTotal: 15,
      defaultRole: OrgRole.MEMBER,
      color: DS.blue700,
      security: {
         ssoEnabled: false,
         enforceTwoFactor: true,
         guestLinksEnabled: true,
         publicBoardsEnabled: false,
         domainCaptureEnabled: true,
      },
   };
}

interface IMemberSeedOpts {
   role: OrgRole;
   status: MemberStatus;
   title: string;
   department: string;
   seat: SeatType;
   twoFactor: boolean;
   joined: string;
   lastActive: string;
   /** Display-cased username; the normalized handle is its lowercase form. */
   displayUsername?: string;
   emailVerified?: boolean;
   appAdmin?: boolean;
   banned?: {reason: string; expiresLabel?: string};
}

function mkMember(id: string, name: string, color: string, opts: IMemberSeedOpts): IMember {
   const first_name = name.split(' ')[0];
   const display_username = opts.displayUsername ?? first_name.toLowerCase();

   return {
      id,
      name,
      email: `${first_name.toLowerCase()}@acme.com`,
      emailVerified: opts.emailVerified ?? true,
      username: display_username.toLowerCase(),
      displayUsername: display_username,
      initials: name
         .split(' ')
         .map((part) => part[0])
         .join('')
         .toUpperCase(),
      color,
      orgRole: opts.role,
      status: opts.status,
      isAppAdmin: opts.appAdmin ?? false,
      isBanned: opts.banned != null,
      banReason: opts.banned?.reason ?? null,
      banExpiresLabel: opts.banned?.expiresLabel ?? null,
      title: opts.title,
      department: opts.department,
      seat: opts.seat,
      twoFactorEnabled: opts.twoFactor,
      joinedLabel: opts.joined,
      lastActiveLabel: opts.lastActive,
   };
}

function createMembers(): IMember[] {
   const mk = mkMember;

   return [
      mk('kg', 'Kelly Bennet', DS.blue700, {
         role: OrgRole.OWNER, status: MemberStatus.ACTIVE, title: 'Head of Product',
         department: 'Product', seat: SeatType.FULL, twoFactor: true,
         joined: 'Jan 2024', lastActive: 'Just now',
         displayUsername: 'KellyG', appAdmin: true,
      }),
      mk('ah', 'Ana Huang', DS.orange500, {
         role: OrgRole.ADMIN, status: MemberStatus.ACTIVE, title: 'Product Designer',
         department: 'Design', seat: SeatType.FULL, twoFactor: true,
         joined: 'Feb 2024', lastActive: '12m ago',
         appAdmin: true,
      }),
      mk('jt', 'Jamie Tran', DS.green600, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Staff Engineer',
         department: 'Engineering', seat: SeatType.FULL, twoFactor: true,
         joined: 'Feb 2024', lastActive: '1h ago',
      }),
      mk('ar', 'Alex Rivera', DS.purple600, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Design Systems Lead',
         department: 'Design', seat: SeatType.FULL, twoFactor: false,
         joined: 'Mar 2024', lastActive: '3h ago',
      }),
      mk('ds', 'Devin Shah', DS.magenta500, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Backend Engineer',
         department: 'Engineering', seat: SeatType.FULL, twoFactor: true,
         joined: 'Apr 2024', lastActive: '2h ago',
      }),
      mk('pn', 'Priya Nair', DS.red500, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Content Strategist',
         department: 'Marketing', seat: SeatType.FULL, twoFactor: false,
         joined: 'Apr 2024', lastActive: '5h ago',
      }),
      mk('ro', 'Rob Olsen', DS.teal600, {
         role: OrgRole.MEMBER, status: MemberStatus.SUSPENDED, title: 'Sales Engineer',
         department: 'Operations', seat: SeatType.LIGHT, twoFactor: false,
         joined: 'May 2024', lastActive: '3w ago',
         banned: {reason: 'Sharing account credentials', expiresLabel: 'in 30 days'},
      }),
      mk('ml', 'Mara Lopez', DS.purple500, {
         role: OrgRole.GUEST, status: MemberStatus.PENDING, title: 'Freelance Writer',
         department: 'Marketing', seat: SeatType.VIEWER, twoFactor: false,
         joined: 'Pending', lastActive: '—',
         emailVerified: false,
      }),
      mk('tw', 'Tom Walsh', DS.blue500, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Account Executive',
         department: 'Operations', seat: SeatType.FULL, twoFactor: true,
         joined: 'May 2024', lastActive: '1d ago',
      }),
      mk('sc', 'Sofia Castro', DS.green500, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Customer Success Mgr',
         department: 'Operations', seat: SeatType.LIGHT, twoFactor: true,
         joined: 'Jun 2024', lastActive: '4h ago',
      }),
      mk('nk', 'Noah Kim', DS.orange700, {
         role: OrgRole.MEMBER, status: MemberStatus.ACTIVE, title: 'Onboarding Lead',
         department: 'Operations', seat: SeatType.LIGHT, twoFactor: false,
         joined: 'Jun 2024', lastActive: '6h ago',
      }),
   ];
}

function createFolderTree(): IFolderTreeNode[] {
   const folder = (id: string, name: string, children: IFolderTreeNode[]): IFolderTreeNode => ({
      type: FolderNodeType.FOLDER, id, name, children,
   });
   const board = (id: string): IFolderTreeNode => ({type: FolderNodeType.BOARD, id});

   return [
      folder('product', 'Product', [
         board('roadmap'),
         board('sprint24'),
         folder('research', 'Research', [board('interviews'), board('usability')]),
      ]),
      folder('engineering', 'Engineering', [board('bugs'), board('mobile'), board('infra')]),
      folder('marketing', 'Marketing', [board('campaigns'), board('content')]),
      folder('operations', 'Operations', [board('sales'), board('success')]),
      board('design'),
   ];
}

const mkInvitation = (
   id: string,
   org_id: string,
   email: string,
   role: OrgRole,
   opts: {
      status: InvitationStatus;
      inviter: string;
      inviterEmail: string;
      sent: string;
      expires: string;
   },
): IInvitation => ({
   id,
   organizationId: org_id,
   email,
   role,
   status: opts.status,
   inviterName: opts.inviter,
   inviterEmail: opts.inviterEmail,
   sentLabel: opts.sent,
   expiresLabel: opts.expires,
});

/**
 * Invitations: pending and expired rows sent by Acme, plus one addressed to
 * the demo user (kelly@acme.com) from Riverside Labs.
 */
function createInvitations(): IInvitation[] {
   const mk = mkInvitation;

   return [
      mk('inv-dana', 'acme', 'dana@northwind.co', OrgRole.MEMBER, {
         status: InvitationStatus.PENDING, inviter: 'Kelly Bennet',
         inviterEmail: 'kelly@acme.com', sent: '2 days ago', expires: 'in 5 days',
      }),
      mk('inv-marco', 'acme', 'marco@brightbakery.com', OrgRole.GUEST, {
         status: InvitationStatus.PENDING, inviter: 'Ana Huang',
         inviterEmail: 'ana@acme.com', sent: 'Yesterday', expires: 'in 6 days',
      }),
      mk('inv-lee', 'acme', 'lee@globex.com', OrgRole.MEMBER, {
         status: InvitationStatus.EXPIRED, inviter: 'Kelly Bennet',
         inviterEmail: 'kelly@acme.com', sent: '3 weeks ago', expires: 'expired Jun 22',
      }),
      mk('inv-kelly', 'riverside', 'kelly@acme.com', OrgRole.ADMIN, {
         status: InvitationStatus.PENDING, inviter: 'Sam Whitfield',
         inviterEmail: 'sam@riverside.dev', sent: '4 hours ago', expires: 'in 7 days',
      }),
   ];
}

const mkApiKey = (
   id: string,
   owner_type: ApiKeyOwnerType,
   owner_id: string,
   name: string,
   start: string,
   opts: {
      created: string;
      expires: string | null;
      enabled: boolean;
      lastRequest: string | null;
      requests: number;
   },
): IApiKey => ({
   id,
   ownerType: owner_type,
   ownerId: owner_id,
   name,
   start,
   createdLabel: opts.created,
   expiresLabel: opts.expires,
   enabled: opts.enabled,
   lastRequestLabel: opts.lastRequest,
   requestCount: opts.requests,
});

/** API keys: two user-owned keys for the demo user plus org-owned keys for Acme. */
function createApiKeys(): IApiKey[] {
   const mk = mkApiKey;

   return [
      mk('key-cli', ApiKeyOwnerType.USER, 'kg', 'Local CLI', 'ab_live_k3xf', {
         created: 'May 12, 2026', expires: null, enabled: true,
         lastRequest: '2 hours ago', requests: 1284,
      }),
      mk('key-zapier', ApiKeyOwnerType.USER, 'kg', 'Zapier integration', 'ab_live_9qtm', {
         created: 'Mar 3, 2026', expires: 'Sep 3, 2026', enabled: false,
         lastRequest: 'Jun 1, 2026', requests: 4102,
      }),
      mk('key-ci', ApiKeyOwnerType.ORGANIZATION, 'acme', 'CI exports', 'ab_live_p7ws', {
         created: 'Apr 22, 2026', expires: null, enabled: true,
         lastRequest: '14 minutes ago', requests: 20871,
      }),
      mk('key-audit', ApiKeyOwnerType.ORGANIZATION, 'acme', 'Compliance audit feed', 'ab_live_c2rh', {
         created: 'Jun 2, 2026', expires: 'Dec 31, 2026', enabled: true,
         lastRequest: 'Yesterday', requests: 356,
      }),
   ];
}

function createNotifications(): INotification[] {
   return [
      {memberId: 'ah', text: 'Ana mentioned you in “Pricing page revamp”', whenLabel: '20m', boardName: 'Product Roadmap', isRead: false},
      {memberId: 'ds', text: 'Devin moved “Public API v2” to In Progress', whenLabel: '1h', boardName: 'Product Roadmap', isRead: false},
      {memberId: 'jt', text: 'Jamie added you to Sprint 24', whenLabel: '3h', boardName: 'Sprint 24', isRead: false},
      {memberId: 'pn', text: 'Priya commented on “Design user onboarding flow”', whenLabel: '5h', boardName: 'Product Roadmap', isRead: false},
   ];
}

// ---------------------------------------------------------------------------
// Boards + cards
// ---------------------------------------------------------------------------

interface ICardOpts {
   description?: string;
   labelIds?: string[];
   fieldValues?: Record<string, string | number | boolean>;
}

function mkCard(
   id: string,
   title: string,
   status: CardStatus,
   priority: CardPriority,
   assignee_id: string | null,
   due_date: string | null,
   opts: ICardOpts = {},
): ICard {
   return {
      id,
      title,
      status,
      priority,
      assigneeId: assignee_id,
      dueDate: due_date,
      description: opts.description ?? '',
      labelIds: opts.labelIds ?? [],
      fieldValues: opts.fieldValues ?? {},
      createdDate: null,
      watcherIds: [],
      checklist: [],
      comments: [],
      activity: [],
   };
}

function mkBoard(
   id: string,
   name: string,
   color: string,
   opts: {
      isStarred?: boolean;
      description: string;
      memberIds: string[];
      labels: IBoard['labels'];
      fields: IBoard['fields'];
      cards: ICard[];
   },
): IBoard {
   return {
      id,
      name,
      color,
      isStarred: opts.isStarred ?? false,
      description: opts.description,
      visibility: BoardVisibility.WORKSPACE,
      memberIds: opts.memberIds,
      labels: opts.labels,
      fields: opts.fields,
      cards: opts.cards,
      share: [],
   };
}

const {TODO, IN_PROGRESS, DONE} = CardStatus;
const {LOW, MEDIUM, HIGH, CRITICAL} = CardPriority;

function createBoards(): IBoard[] {
   return [
      mkBoard('roadmap', 'Product Roadmap', DS.blue600, {
         isStarred: true,
         description: 'Quarterly product priorities, owners, and progress across the Amazing Boards team.',
         memberIds: ['kg', 'ah', 'ds', 'pn', 'jt'],
         labels: [
            {id: 'feat', name: 'Feature', color: DS.green500},
            {id: 'design', name: 'Design', color: DS.purple500},
            {id: 'ops', name: 'Ops', color: DS.teal500},
         ],
         fields: [
            {id: 'quarter', name: 'Quarter', type: CustomFieldType.DROPDOWN, options: ['Q2', 'Q3', 'Q4']},
            {id: 'effort', name: 'Effort', type: CustomFieldType.NUMBER},
            {id: 'progress', name: 'Progress', type: CustomFieldType.PROGRESS},
         ],
         cards: [
            mkCard('c1', 'Design user onboarding flow', IN_PROGRESS, HIGH, 'kg', '2026-06-15', {
               description: 'Map the first-run experience from sign-up to first populated board.',
               labelIds: ['feat', 'design'],
               fieldValues: {quarter: 'Q2', effort: 5, progress: 60},
            }),
            mkCard('c2', 'Pricing page revamp', TODO, MEDIUM, 'ah', '2026-06-23', {
               labelIds: ['design'], fieldValues: {quarter: 'Q2', effort: 3, progress: 10},
            }),
            mkCard('c3', 'Public API v2', IN_PROGRESS, HIGH, 'ds', '2026-06-27', {
               description: 'Token auth, pagination, and webhooks.',
               labelIds: ['feat'], fieldValues: {quarter: 'Q3', effort: 8, progress: 35},
            }),
            mkCard('c4', 'Mobile push notifications', TODO, LOW, null, '2026-07-02', {
               fieldValues: {quarter: 'Q3', effort: 5, progress: 0},
            }),
            mkCard('c5', 'Quarterly OKR review', DONE, MEDIUM, 'kg', '2026-06-09', {
               labelIds: ['ops'], fieldValues: {quarter: 'Q2', effort: 2, progress: 100},
            }),
            mkCard('c6', 'Roadmap sync with sales', TODO, MEDIUM, 'pn', '2026-06-19', {
               fieldValues: {quarter: 'Q2', effort: 1, progress: 0},
            }),
            mkCard('c7', 'Deprecate legacy dashboard', IN_PROGRESS, HIGH, 'jt', '2026-06-30', {
               labelIds: ['ops'], fieldValues: {quarter: 'Q3', effort: 5, progress: 45},
            }),
         ],
      }),

      mkBoard('sprint24', 'Sprint 24', DS.purple500, {
         isStarred: true,
         description: 'Current two-week sprint. Keep WIP low and ship.',
         memberIds: ['kg', 'ds', 'ar', 'jt', 'ah', 'pn'],
         labels: [
            {id: 'eng', name: 'Eng', color: DS.blue500},
            {id: 'design', name: 'Design', color: DS.purple500},
            {id: 'chore', name: 'Chore', color: DS.neutral400},
         ],
         fields: [
            {id: 'points', name: 'Story points', type: CustomFieldType.NUMBER},
            {id: 'sprint', name: 'Sprint', type: CustomFieldType.DROPDOWN, options: ['S23', 'S24']},
            {id: 'reviewer', name: 'Reviewer', type: CustomFieldType.MEMBER},
         ],
         cards: [
            mkCard('c8', 'Refactor authentication', IN_PROGRESS, CRITICAL, 'kg', '2026-06-14', {
               description: 'Move to token-based auth and add refresh handling.',
               labelIds: ['eng'], fieldValues: {points: 8, sprint: 'S24', reviewer: 'ds'},
            }),
            mkCard('c9', 'Fix flaky CI tests', TODO, HIGH, 'ds', '2026-06-16', {
               labelIds: ['chore'], fieldValues: {points: 3, sprint: 'S24'},
            }),
            mkCard('c10', 'Add dark mode tokens', IN_PROGRESS, MEDIUM, 'ar', '2026-06-20', {
               labelIds: ['design'], fieldValues: {points: 5, sprint: 'S24', reviewer: 'kg'},
            }),
            mkCard('c11', 'Card drag performance', DONE, HIGH, 'jt', '2026-06-11', {
               labelIds: ['eng'], fieldValues: {points: 5, sprint: 'S24'},
            }),
            mkCard('c12', 'Empty-state illustrations', TODO, LOW, 'ah', '2026-06-24', {
               labelIds: ['design'], fieldValues: {points: 2, sprint: 'S24'},
            }),
            mkCard('c13', 'Release notes 24.2', DONE, MEDIUM, 'pn', '2026-06-12', {
               labelIds: ['chore'], fieldValues: {points: 1, sprint: 'S24'},
            }),
         ],
      }),

      mkBoard('interviews', 'User Interviews', DS.teal500, {
         description: 'Research sessions with customers and prospects.',
         memberIds: ['ah', 'pn', 'kg'],
         labels: [
            {id: 'smb', name: 'SMB', color: DS.teal500},
            {id: 'ent', name: 'Enterprise', color: DS.blue500},
         ],
         fields: [
            {id: 'participant', name: 'Participant', type: CustomFieldType.TEXT},
            {id: 'segment', name: 'Segment', type: CustomFieldType.DROPDOWN, options: ['SMB', 'Mid-market', 'Enterprise']},
            {id: 'score', name: 'Score', type: CustomFieldType.NUMBER},
         ],
         cards: [
            mkCard('c14', 'Interview: Northwind Co', DONE, MEDIUM, 'ah', '2026-06-05', {
               labelIds: ['ent'], fieldValues: {participant: 'Dana W.', segment: 'Enterprise', score: 8},
            }),
            mkCard('c15', 'Interview: Bright Bakery', DONE, LOW, 'pn', '2026-06-08', {
               labelIds: ['smb'], fieldValues: {participant: 'Marco P.', segment: 'SMB', score: 7},
            }),
            mkCard('c16', 'Synthesize onboarding pains', IN_PROGRESS, HIGH, 'ah', '2026-06-17', {
               fieldValues: {segment: 'Mid-market', score: 0},
            }),
            mkCard('c17', 'Recruit 5 power users', TODO, MEDIUM, 'pn', '2026-06-26'),
            mkCard('c18', 'Share findings deck', TODO, MEDIUM, 'kg', '2026-07-01'),
         ],
      }),

      mkBoard('usability', 'Usability Tests', DS.green500, {
         description: 'Task-based usability sessions on new flows.',
         memberIds: ['ah', 'ar', 'kg'],
         labels: [
            {id: 'pass', name: 'Pass', color: DS.green500},
            {id: 'fail', name: 'Issue', color: DS.red500},
         ],
         fields: [
            {id: 'flow', name: 'Flow', type: CustomFieldType.DROPDOWN, options: ['Onboarding', 'Sharing', 'Billing']},
            {id: 'success', name: 'Success rate', type: CustomFieldType.NUMBER},
         ],
         cards: [
            mkCard('c19', 'Test new share dialog', IN_PROGRESS, HIGH, 'ar', '2026-06-18', {
               labelIds: ['fail'], fieldValues: {flow: 'Sharing', success: 60},
            }),
            mkCard('c20', 'Test onboarding checklist', DONE, MEDIUM, 'ah', '2026-06-10', {
               labelIds: ['pass'], fieldValues: {flow: 'Onboarding', success: 90},
            }),
            mkCard('c21', 'Test billing upgrade', TODO, MEDIUM, 'kg', '2026-06-29', {
               fieldValues: {flow: 'Billing'},
            }),
            mkCard('c22', 'Write moderation script', TODO, LOW, 'ah', '2026-07-03'),
         ],
      }),

      mkBoard('bugs', 'Bugs & Triage', DS.red500, {
         description: 'Incoming bugs, triaged by severity.',
         memberIds: ['ds', 'jt', 'pn', 'ar', 'kg'],
         labels: [
            {id: 's1', name: 'S1', color: DS.red500},
            {id: 's2', name: 'S2', color: DS.orange400},
            {id: 's3', name: 'S3', color: DS.yellow500},
         ],
         fields: [
            {id: 'severity', name: 'Severity', type: CustomFieldType.DROPDOWN, options: ['S1', 'S2', 'S3']},
            {id: 'steps', name: 'Steps to reproduce', type: CustomFieldType.TEXT},
            {id: 'fixedin', name: 'Fixed in', type: CustomFieldType.TEXT},
         ],
         cards: [
            mkCard('c23', 'Login loops on Safari', IN_PROGRESS, CRITICAL, 'ds', '2026-06-13', {
               description: 'Session cookie not set on Safari 17.',
               labelIds: ['s1'], fieldValues: {severity: 'S1', steps: 'Sign in on Safari → redirect loop'},
            }),
            mkCard('c24', 'Board fails to load >500 cards', TODO, HIGH, 'jt', '2026-06-18', {
               labelIds: ['s2'], fieldValues: {severity: 'S2'},
            }),
            mkCard('c25', 'Avatar upload 500 error', IN_PROGRESS, HIGH, 'pn', '2026-06-17', {
               labelIds: ['s2'], fieldValues: {severity: 'S2', steps: 'Upload >5MB PNG'},
            }),
            mkCard('c26', 'Drag ghost stuck on drop', DONE, MEDIUM, 'ar', '2026-06-10', {
               labelIds: ['s3'], fieldValues: {severity: 'S3', fixedin: '24.1.3'},
            }),
            mkCard('c27', 'Timezone off by one on due', TODO, LOW, null, '2026-06-26', {
               labelIds: ['s3'], fieldValues: {severity: 'S3'},
            }),
            mkCard('c28', 'Webhook retries duplicate', TODO, HIGH, 'ds', '2026-07-01', {
               labelIds: ['s2'], fieldValues: {severity: 'S2'},
            }),
            mkCard('c29', 'CSV export truncates rows', DONE, MEDIUM, 'kg', '2026-06-08', {
               labelIds: ['s3'], fieldValues: {severity: 'S3', fixedin: '24.1.2'},
            }),
         ],
      }),

      mkBoard('mobile', 'Mobile App', DS.blue500, {
         description: 'iOS + Android native app work.',
         memberIds: ['pn', 'ds', 'ar', 'jt', 'ah'],
         labels: [
            {id: 'ios', name: 'iOS', color: DS.neutral700},
            {id: 'android', name: 'Android', color: DS.green600},
         ],
         fields: [
            {id: 'platform', name: 'Platform', type: CustomFieldType.DROPDOWN, options: ['iOS', 'Android', 'Both']},
            {id: 'build', name: 'Build', type: CustomFieldType.TEXT},
         ],
         cards: [
            mkCard('c30', 'Offline sync engine', IN_PROGRESS, HIGH, 'pn', '2026-06-21', {
               description: 'Queue mutations and replay on reconnect.',
               labelIds: ['ios', 'android'], fieldValues: {platform: 'Both', build: '142'},
            }),
            mkCard('c31', 'Biometric unlock', TODO, MEDIUM, 'ds', '2026-06-28', {
               labelIds: ['ios'], fieldValues: {platform: 'iOS'},
            }),
            mkCard('c32', 'iPad split view', TODO, LOW, 'ar', '2026-07-04', {
               labelIds: ['ios'], fieldValues: {platform: 'iOS'},
            }),
            mkCard('c33', 'Crash on cold start', DONE, CRITICAL, 'jt', '2026-06-09', {
               labelIds: ['android'], fieldValues: {platform: 'Android', build: '140'},
            }),
            mkCard('c34', 'App Store screenshots', IN_PROGRESS, MEDIUM, 'ah', '2026-06-22', {
               fieldValues: {platform: 'Both'},
            }),
         ],
      }),

      mkBoard('infra', 'Infrastructure', DS.teal700, {
         description: 'Platform, reliability, and cost work.',
         memberIds: ['ds', 'jt', 'pn', 'kg'],
         labels: [
            {id: 'db', name: 'Database', color: DS.blue500},
            {id: 'ci', name: 'CI/CD', color: DS.purple500},
         ],
         fields: [
            {id: 'env', name: 'Environment', type: CustomFieldType.DROPDOWN, options: ['Dev', 'Staging', 'Prod']},
            {id: 'owner', name: 'Owner', type: CustomFieldType.MEMBER},
         ],
         cards: [
            mkCard('c35', 'Migrate to Postgres 16', IN_PROGRESS, HIGH, 'ds', '2026-06-25', {
               labelIds: ['db'], fieldValues: {env: 'Prod', owner: 'ds'},
            }),
            mkCard('c36', 'Add read replicas', TODO, MEDIUM, 'jt', '2026-07-03', {
               labelIds: ['db'], fieldValues: {env: 'Prod'},
            }),
            mkCard('c37', 'Terraform module cleanup', DONE, LOW, 'pn', '2026-06-07', {
               labelIds: ['ci'], fieldValues: {env: 'Staging'},
            }),
            mkCard('c38', 'Incident runbook v2', TODO, MEDIUM, 'kg', '2026-06-29'),
         ],
      }),

      mkBoard('campaigns', 'Campaigns Q2', DS.magenta500, {
         description: 'Demand-gen campaigns for the quarter.',
         memberIds: ['ah', 'ml', 'pn'],
         labels: [
            {id: 'email', name: 'Email', color: DS.blue500},
            {id: 'paid', name: 'Paid', color: DS.magenta500},
            {id: 'event', name: 'Event', color: DS.orange400},
         ],
         fields: [
            {id: 'channel', name: 'Channel', type: CustomFieldType.DROPDOWN, options: ['Email', 'Social', 'Paid', 'Event']},
            {id: 'budget', name: 'Budget', type: CustomFieldType.NUMBER},
            {id: 'launch', name: 'Launch', type: CustomFieldType.DATE},
         ],
         cards: [
            mkCard('c39', 'Summer launch email', IN_PROGRESS, HIGH, 'ah', '2026-06-16', {
               description: 'Announce the new views to all users.',
               labelIds: ['email'], fieldValues: {channel: 'Email', budget: 0, launch: '2026-06-16'},
            }),
            mkCard('c40', 'LinkedIn ad set', TODO, MEDIUM, 'ml', '2026-06-20', {
               labelIds: ['paid'], fieldValues: {channel: 'Paid', budget: 4000},
            }),
            mkCard('c41', 'Webinar: Power-Ups 101', DONE, MEDIUM, 'pn', '2026-06-06', {
               labelIds: ['event'], fieldValues: {channel: 'Event', budget: 1200, launch: '2026-06-06'},
            }),
            mkCard('c42', 'Influencer outreach', TODO, LOW, 'ml', '2026-07-02', {
               fieldValues: {channel: 'Social'},
            }),
            mkCard('c43', 'Landing page A/B test', IN_PROGRESS, HIGH, 'ah', '2026-06-24', {
               labelIds: ['paid'], fieldValues: {channel: 'Paid', budget: 800},
            }),
         ],
      }),

      mkBoard('content', 'Content Calendar', DS.orange400, {
         description: 'Editorial pipeline across blog, video, and guides.',
         memberIds: ['pn', 'ah', 'ml', 'ar'],
         labels: [
            {id: 'blog', name: 'Blog', color: DS.orange400},
            {id: 'video', name: 'Video', color: DS.red500},
            {id: 'guide', name: 'Guide', color: DS.teal500},
         ],
         fields: [
            {id: 'format', name: 'Format', type: CustomFieldType.DROPDOWN, options: ['Blog', 'Video', 'Guide']},
            {id: 'author', name: 'Author', type: CustomFieldType.MEMBER},
            {id: 'publish', name: 'Publish', type: CustomFieldType.DATE},
         ],
         cards: [
            mkCard('c44', '"10 Trello tips" blog post', IN_PROGRESS, MEDIUM, 'pn', '2026-06-18', {
               labelIds: ['blog'], fieldValues: {format: 'Blog', author: 'pn', publish: '2026-06-18'},
            }),
            mkCard('c45', 'Onboarding walkthrough video', TODO, HIGH, 'ah', '2026-06-27', {
               labelIds: ['video'], fieldValues: {format: 'Video', author: 'ah'},
            }),
            mkCard('c46', 'Customer story: Acme', DONE, MEDIUM, 'ml', '2026-06-10', {
               labelIds: ['blog'], fieldValues: {format: 'Blog', author: 'ml', publish: '2026-06-10'},
            }),
            mkCard('c47', 'SEO refresh Q2', TODO, LOW, 'pn', '2026-07-05', {
               fieldValues: {format: 'Guide'},
            }),
            mkCard('c48', 'Changelog digest', IN_PROGRESS, LOW, 'ar', '2026-06-22', {
               labelIds: ['guide'], fieldValues: {format: 'Guide', author: 'ar'},
            }),
         ],
      }),

      mkBoard('design', 'Design System', DS.purple600, {
         isStarred: true,
         description: 'Shared components, tokens, and guidelines.',
         memberIds: ['ar', 'kg', 'ah', 'ds'],
         labels: [
            {id: 'token', name: 'Tokens', color: DS.purple500},
            {id: 'comp', name: 'Component', color: DS.blue500},
            {id: 'a11y', name: 'A11y', color: DS.green600},
         ],
         fields: [
            {id: 'component', name: 'Component', type: CustomFieldType.TEXT},
            {id: 'adoption', name: 'Adoption', type: CustomFieldType.PROGRESS},
         ],
         cards: [
            mkCard('c49', 'Button component audit', IN_PROGRESS, MEDIUM, 'ar', '2026-06-19', {
               labelIds: ['comp'], fieldValues: {component: 'Button', adoption: 70},
            }),
            mkCard('c50', 'Color token cleanup', DONE, HIGH, 'ar', '2026-06-11', {
               labelIds: ['token'], fieldValues: {component: 'Color', adoption: 100},
            }),
            mkCard('c51', 'Form field spec', TODO, MEDIUM, 'kg', '2026-06-26', {
               labelIds: ['comp'], fieldValues: {component: 'Field', adoption: 20},
            }),
            mkCard('c52', 'Icon set v3', IN_PROGRESS, LOW, 'ah', '2026-07-01', {
               fieldValues: {component: 'Icon', adoption: 40},
            }),
            mkCard('c53', 'Accessibility pass', TODO, HIGH, 'ds', '2026-06-30', {
               labelIds: ['a11y'], fieldValues: {adoption: 0},
            }),
         ],
      }),

      mkBoard('sales', 'Sales Pipeline', DS.green700, {
         description: 'Deals in flight, by stage. Keep next steps current.',
         memberIds: ['tw', 'sc', 'pn', 'kg'],
         labels: [
            {id: 'inbound', name: 'Inbound', color: DS.green500},
            {id: 'outbound', name: 'Outbound', color: DS.blue500},
            {id: 'renewal', name: 'Renewal', color: DS.purple500},
         ],
         fields: [
            {id: 'value', name: 'Deal value', type: CustomFieldType.NUMBER},
            {id: 'stage', name: 'Stage', type: CustomFieldType.DROPDOWN, options: ['Lead', 'Demo', 'Proposal', 'Won']},
            {id: 'rep', name: 'Owner', type: CustomFieldType.MEMBER},
         ],
         cards: [
            mkCard('c54', 'Northwind Co — 25 seats', IN_PROGRESS, HIGH, 'tw', '2026-06-20', {
               description: 'Mid-market expansion. Security review in progress.',
               labelIds: ['inbound'], fieldValues: {value: 30000, stage: 'Proposal', rep: 'tw'},
            }),
            mkCard('c55', 'Bright Bakery — starter', TODO, MEDIUM, 'sc', '2026-06-24', {
               labelIds: ['inbound'], fieldValues: {value: 1200, stage: 'Demo', rep: 'sc'},
            }),
            mkCard('c56', 'Globex renewal', IN_PROGRESS, CRITICAL, 'tw', '2026-06-28', {
               description: 'Annual renewal, at risk — exec sponsor changed.',
               labelIds: ['renewal'], fieldValues: {value: 48000, stage: 'Proposal', rep: 'tw'},
            }),
            mkCard('c57', 'Initech outbound', TODO, LOW, 'sc', '2026-07-03', {
               labelIds: ['outbound'], fieldValues: {value: 8000, stage: 'Lead', rep: 'sc'},
            }),
            mkCard('c58', 'Umbrella Corp — closed', DONE, HIGH, 'tw', '2026-06-08', {
               labelIds: ['inbound'], fieldValues: {value: 62000, stage: 'Won', rep: 'tw'},
            }),
            mkCard('c59', 'Stark Industries demo', IN_PROGRESS, MEDIUM, 'pn', '2026-06-26', {
               fieldValues: {value: 15000, stage: 'Demo', rep: 'pn'},
            }),
         ],
      }),

      mkBoard('success', 'Customer Success', DS.teal600, {
         description: 'Onboarding, health checks, and support escalations.',
         memberIds: ['sc', 'nk', 'pn', 'ah'],
         labels: [
            {id: 'onboard', name: 'Onboarding', color: DS.blue500},
            {id: 'health', name: 'Health check', color: DS.green500},
            {id: 'escalation', name: 'Escalation', color: DS.red500},
         ],
         fields: [
            {id: 'account', name: 'Account', type: CustomFieldType.TEXT},
            {id: 'health', name: 'Health', type: CustomFieldType.DROPDOWN, options: ['Green', 'Yellow', 'Red']},
            {id: 'csm', name: 'CSM', type: CustomFieldType.MEMBER},
         ],
         cards: [
            mkCard('c60', 'Onboard Northwind Co', IN_PROGRESS, HIGH, 'nk', '2026-06-21', {
               description: 'Kickoff done; migrating 4 legacy boards.',
               labelIds: ['onboard'], fieldValues: {account: 'Northwind', health: 'Green', csm: 'nk'},
            }),
            mkCard('c61', 'QBR — Globex', TODO, HIGH, 'sc', '2026-06-27', {
               labelIds: ['health'], fieldValues: {account: 'Globex', health: 'Yellow', csm: 'sc'},
            }),
            mkCard('c62', 'Escalation: API limits', IN_PROGRESS, CRITICAL, 'pn', '2026-06-19', {
               description: 'Customer hitting rate limits on bulk import.',
               labelIds: ['escalation'], fieldValues: {account: 'Initech', health: 'Red', csm: 'pn'},
            }),
            mkCard('c63', 'Health check: Bright Bakery', DONE, MEDIUM, 'nk', '2026-06-09', {
               labelIds: ['health'], fieldValues: {account: 'Bright Bakery', health: 'Green', csm: 'nk'},
            }),
            mkCard('c64', 'Renewal prep: Umbrella', TODO, MEDIUM, 'sc', '2026-07-02', {
               fieldValues: {account: 'Umbrella', health: 'Green', csm: 'sc'},
            }),
         ],
      }),
   ];
}

// ---------------------------------------------------------------------------
// UUID conversion — rewrite readable seed keys to deterministic UUIDs
// ---------------------------------------------------------------------------

const memberUuid = (key: string): string => stableSeedUuid(`member:${key}`);
const boardUuid = (key: string): string => stableSeedUuid(`board:${key}`);
const cardUuid = (key: string): string => stableSeedUuid(`card:${key}`);
const folderUuid = (key: string): string => stableSeedUuid(`folder:${key}`);
const orgUuid = (key: string): string => stableSeedUuid(`org:${key}`);
const invitationUuid = (key: string): string => stableSeedUuid(`invitation:${key}`);
const apiKeyUuid = (key: string): string => stableSeedUuid(`apikey:${key}`);
const labelUuid = (board_key: string, key: string): string =>
   stableSeedUuid(`label:${board_key}:${key}`);
const fieldUuid = (board_key: string, key: string): string =>
   stableSeedUuid(`field:${board_key}:${key}`);

/**
 * Rewrite every entity id and cross-reference in the seed blob from its
 * readable authoring key to its deterministic UUID. Runs last, after
 * enrichment (which works with the readable keys).
 */
function uuidifySeed(data: IBoardsSeedData): void {
   data.organization.id = orgUuid(data.organization.id);

   for (const member of data.members) {
      member.id = memberUuid(member.id);
   }
   data.currentUserId = memberUuid(data.currentUserId);
   for (const notification of data.notifications) {
      notification.memberId = memberUuid(notification.memberId);
   }

   for (const invitation of data.invitations) {
      invitation.id = invitationUuid(invitation.id);
      invitation.organizationId = orgUuid(invitation.organizationId);
   }
   for (const api_key of data.apiKeys) {
      api_key.id = apiKeyUuid(api_key.id);
      api_key.ownerId =
         api_key.ownerType === ApiKeyOwnerType.USER
            ? memberUuid(api_key.ownerId)
            : orgUuid(api_key.ownerId);
   }

   for (const board of data.boards) {
      uuidifyBoard(board);
   }
   data.currentBoardId = boardUuid(data.currentBoardId);

   uuidifyFolderNodes(data.folderTree);
   data.openFolderIds = data.openFolderIds.map((key) => folderUuid(key));
}

function uuidifyBoard(board: IBoard): void {
   const board_key = board.id;

   // Member-type field ids, captured before rewriting, so their card values
   // (member keys) can be converted too.
   const member_field_keys = new Set(
      board.fields.filter((f) => f.type === CustomFieldType.MEMBER).map((f) => f.id),
   );

   for (const field of board.fields) {
      field.id = fieldUuid(board_key, field.id);
   }
   for (const label of board.labels) {
      label.id = labelUuid(board_key, label.id);
   }

   for (const card of board.cards) {
      card.id = cardUuid(card.id);
      card.assigneeId = card.assigneeId != null ? memberUuid(card.assigneeId) : null;
      card.watcherIds = card.watcherIds.map((key) => memberUuid(key));
      card.labelIds = card.labelIds.map((key) => labelUuid(board_key, key));
      card.fieldValues = Object.fromEntries(
         Object.entries(card.fieldValues).map(([field_key, value]) => [
            fieldUuid(board_key, field_key),
            member_field_keys.has(field_key) && isString(value)
               ? memberUuid(value)
               : value,
         ]),
      );
      for (const comment of card.comments) {
         comment.memberId = memberUuid(comment.memberId);
      }
      for (const activity of card.activity) {
         activity.memberId = memberUuid(activity.memberId);
      }
   }

   board.memberIds = board.memberIds.map((key) => memberUuid(key));
   for (const share of board.share) {
      share.memberId = memberUuid(share.memberId);
   }
   board.id = boardUuid(board_key);
}

function uuidifyFolderNodes(nodes: IFolderTreeNode[]): void {
   for (const node of nodes) {
      if (node.type === FolderNodeType.FOLDER) {
         node.id = folderUuid(node.id);
         uuidifyFolderNodes(node.children ?? []);
      } else {
         node.id = boardUuid(node.id);
      }
   }
}

// ---------------------------------------------------------------------------
// Share entries + deterministic card enrichment
// ---------------------------------------------------------------------------

const ACCESS_CYCLE: readonly BoardAccess[] = [
   BoardAccess.EDIT,
   BoardAccess.EDIT,
   BoardAccess.COMMENT,
   BoardAccess.VIEW,
];

function assignBoardShares(boards: IBoard[]): void {
   for (const board of boards) {
      board.share = board.memberIds.map((member_id, idx) => ({
         memberId: member_id,
         access: ACCESS_CYCLE[idx % ACCESS_CYCLE.length],
      }));
   }
}

const DEFAULT_SEED_DUE = '2026-06-15';

/**
 * Fill each card's detail (description, created date, watchers, checklist,
 * comments, activity) from its title, status and assignee.
 */
function enrichSeedCards(boards: IBoard[], members: readonly IMember[]): void {
   const member_by_id = new Map(members.map((m) => [m.id, m]));

   for (const board of boards) {
      for (const card of board.cards) {
         const is_done = card.status === CardStatus.DONE;
         const is_in_progress = card.status === CardStatus.IN_PROGRESS;
         const author =
            (card.assigneeId != null ? member_by_id.get(card.assigneeId) : null) ??
            member_by_id.get(board.memberIds[0]) ??
            members[0];
         const other =
            board.memberIds
               .map((id) => member_by_id.get(id))
               .find((m) => m != null && m.id !== author.id) ?? members[1];

         card.createdDate = computeCreatedDate(card.dueDate);
         card.watcherIds = board.memberIds.slice(0, 3);

         if (card.description === '') {
            card.description = buildDescriptionFallback(board, card, author);
         }

         card.checklist = [
            {text: 'Define scope & acceptance criteria', isDone: true},
            {text: 'Draft approach and share for feedback', isDone: is_done || is_in_progress},
            {
               text: 'Implement first pass',
               isDone: is_done || (is_in_progress && card.priority !== CardPriority.LOW),
            },
            {
               text: `Review with ${board.name.length < 16 ? board.name : 'the team'}`,
               isDone: is_done,
            },
            {text: 'Ship and close out', isDone: is_done},
         ];

         card.comments = buildComments(card, author.id, other.id);
         card.activity = buildActivity(board, card, author.id);
      }
   }
}

/** Created date is 9 days before the due date (or the default seed due date). */
function computeCreatedDate(due_date: string | null): string {
   const due = DateTime.fromISO(due_date ?? DEFAULT_SEED_DUE);
   return due.minus({days: 9}).toISODate() ?? DEFAULT_SEED_DUE;
}

function buildDescriptionFallback(board: IBoard, card: ICard, author: IMember): string {
   const status_text =
      card.status === CardStatus.DONE
         ? 'Completed and verified.'
         : card.status === CardStatus.IN_PROGRESS
           ? 'Actively in progress this week.'
           : 'Queued up and ready to start.';
   const owner_text = card.assigneeId != null ? `, owned by ${author.name}` : '';
   return `${card.title} — part of the ${board.name} board${owner_text}. ${status_text}`;
}

function buildComments(card: ICard, author_id: string, other_id: string): ICardComment[] {
   const comments: ICardComment[] = [];
   if (card.status !== CardStatus.TODO) {
      comments.push({
         memberId: other_id,
         text: 'Picked this up — should have something to look at soon.',
         whenLabel: '2 days ago',
      });
   }
   if (card.priority === CardPriority.CRITICAL || card.priority === CardPriority.HIGH) {
      comments.push({
         memberId: author_id,
         text: 'Bumping priority, this is blocking other work.',
         whenLabel: 'Yesterday',
      });
   }
   if (card.status === CardStatus.DONE) {
      comments.push({
         memberId: author_id,
         text: 'Shipped ✅ closing this out.',
         whenLabel: '4 hours ago',
      });
   }
   return comments;
}

function buildActivity(board: IBoard, card: ICard, author_id: string): ICardActivity[] {
   const created_label =
      card.createdDate != null
         ? (DateTime.fromISO(card.createdDate).toFormat('LLL d') ?? card.createdDate)
         : 'earlier';

   const activity: ICardActivity[] = [
      {memberId: author_id, action: 'created this card', whenLabel: created_label},
   ];
   if (card.assigneeId != null) {
      activity.push({memberId: card.assigneeId, action: 'was assigned', whenLabel: '3 days ago'});
   }
   if (card.status === CardStatus.IN_PROGRESS) {
      activity.push({memberId: author_id, action: 'moved this to In Progress', whenLabel: '2 days ago'});
   }
   if (card.status === CardStatus.DONE) {
      activity.push({memberId: author_id, action: 'marked this Done', whenLabel: '4 hours ago'});
   }
   if (card.labelIds.length > 0) {
      const label = board.labels.find((l) => l.id === card.labelIds[0]);
      activity.push({
         memberId: author_id,
         action: `added the ${label?.name ?? 'a'} label`,
         whenLabel: 'Yesterday',
      });
   }
   return activity;
}
